import ShareIcon from "@Icons/ShareIcon";
import ShareIconAlt from "@Icons/ShareIconAlt";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import styles from "./SharePropertyWrapper.module.scss";
import { AppState } from "@Store/index";
import Portal from "@Components/Portal";
import PropertyDetailsModal from "@Pages/PropertyPage/components/PropertyDetailsModal";
import ShareProperty from "@Pages/PropertyPage/components/ShareProperty";
import useIsDesktop from "hooks/useIsDesktop";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import useWhitelabel from "hooks/useWhitelabel";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";

export const SharePropertyModal = ({
  shareUrl: customShareUrlProp,
  noHistoryPush,
}: {
  shareUrl?: string;
  noHistoryPush?: boolean;
}) => {
  const { showShare, inventoryUrl, canonicalName, shareUrlFromRedux } = useSelector(
    ({ propertyPageData }: AppState) => ({
      showShare: propertyPageData.showShareModal,
      inventoryUrl: propertyPageData.data?.inventoryUrl,
      canonicalName: propertyPageData.data?.canonical_name,
      shareUrlFromRedux: propertyPageData.shareUrl,
    }),
    shallowEqual,
  );
  const { isWhitelabel, isDemoSubdomain, partner_uuid } = useWhitelabel();
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);

  if (!showShare) return null;

  const finalInventoryUrl = customShareUrlProp || shareUrlFromRedux || inventoryUrl;

  return (
    <Portal rootId="forPortal">
      <PropertyDetailsModal
        header="Share Property"
        hidePropertyModal={() => propertyPageDispatcher.showShareModal(false)}
        isDesktop={isDesktop}
        {...(noHistoryPush
          ? { isHistoryPush: false, bottomSheetClassName: styles.elevatedZIndex, noBodyLock: true }
          : {})}
      >
        <ShareProperty
          inventoryUrl={
            isWhitelabel
              ? convertToWhitelabelURL(finalInventoryUrl, isDemoSubdomain, partner_uuid)
              : finalInventoryUrl
          }
          isDesktop={isDesktop}
          canonicalName={canonicalName}
        />
      </PropertyDetailsModal>
    </Portal>
  );
};

const SharePropertyWrapper = ({
  shareUrl,
  className = "",
  fill,
  variant = "default",
}: {
  shareUrl?: string;
  className?: string;
  fill?: string;
  variant?: "default" | "alt";
}) => {
  const { name } = useSelector(({ propertyPageData }: AppState) => ({
    name: propertyPageData.data?.name,
  }));
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);

  return (
    <i
      data-testid="propertypage-property-share-icon"
      className={formatClassNames(styles.shareIcon, className)}
      role="none"
      onClick={() => {
        gtagEventEmitter({
          action: "clicked_share",
          category: PAGE.INVENTORY,
          label: name,
        });
        if (shareUrl) {
          propertyPageDispatcher.showShareModal({ show: true, url: shareUrl });
        } else {
          propertyPageDispatcher.showShareModal(true);
        }
      }}
    >
      {variant === "alt" ? <ShareIconAlt fill={fill} /> : <ShareIcon fill={fill} />}
    </i>
  );
};

export default wrapperHOC(SharePropertyWrapper, {
  componentName: "SharePropertyWrapper-PropertyHeader-PropertyPage",
  showForChina: true,
});
